import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  POLYGON_TYPE,
  CLEAR_ALL_POLYGONS,
  DELETE_POLYGON,
  SET_ZONE_NAME,
  SET_BORDER_LINE_NAME,
  CAMERA_ADDED
} from '../../../../store/actions';
import { createZones } from '../../../../api/FM/videoPageApis';
import {
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';
// import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
// import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
// import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
// import PanToolIcon from '@mui/icons-material/PanTool';

// Define colors for all polygon types
export const polygonColors = {
  zones: '#535BAC',
  frisking_line: '#0000FF'
};

// Predefined colors for random assignment
const colorPalette = [
  // Vibrant primary and secondary colors
  '#FF0000',
  '#00FF00',
  '#0000FF',
  '#FF00FF',
  '#00FFFF',
  '#FFFF00',
  // Bright warm colors
  '#FF4500',
  '#FF6B00',
  '#FF1493',
  '#FF4D4D',
  '#FFA500',
  '#FF69B4',
  // Bright cool colors
  '#00FF7F',
  '#00CED1',
  '#1E90FF',
  '#4169E1',
  '#7B68EE',
  '#9370DB',
  // Rich jewel tones
  '#FF1744',
  '#D500F9',
  '#651FFF',
  '#3D5AFE',
  '#00B0FF',
  '#00E5FF',
  '#1DE9B6',
  '#76FF03',
  '#C6FF00',
  '#FFEA00',
  '#FFC400',
  '#FF9100'
];

const MiddleController = ({ handleCloseAnnotate, cameraId }) => {
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'zone' or 'borderLine'
  const [inputName, setInputName] = useState('');
  const [inputError, setInputError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  const dispatch = useDispatch();
  const currentPolygonType = useSelector((state) => state.cameraReducer.polygonType);
  const polygonData = useSelector((state) => state.cameraReducer.polygonData);
  const zoneNames = useSelector((state) => state.cameraReducer.zoneNames);
  const zoneColors = useSelector((state) => state.cameraReducer.zoneColors);
  const borderLineNames = useSelector((state) => state.cameraReducer.borderLineNames);
  const borderLineColors = useSelector((state) => state.cameraReducer.borderLineColors);

  const getRandomColor = () => {
    const usedColors = new Set([...Object.values(zoneColors || {}), ...Object.values(borderLineColors || {})]);

    // Filter out colors that are already in use
    const availableColors = colorPalette.filter((color) => !usedColors.has(color));

    // If all colors are used, return a random color from the full palette
    if (availableColors.length === 0) {
      return colorPalette[Math.floor(Math.random() * colorPalette.length)];
    }

    // Return a random color from the available colors
    return availableColors[Math.floor(Math.random() * availableColors.length)];
  };

  // const getPolygonTypeName = (type) => {
  //   const names = {
  //     zones: 'Zone',
  //     frisking_line: 'Border Line'
  //   };
  //   return names[type] || type;
  // };

  const handlePolygonTypeSelect = (type) => {
    if (type === 'zones') {
      setDialogType('zone');
      setOpenDialog(true);
    } else if (type === 'frisking_line') {
      setDialogType('borderLine');
      setOpenDialog(true);
    } else {
      dispatch({ type: POLYGON_TYPE, polygonType: type });
    }
  };

  const handleNameSubmit = () => {
    if (!inputName.trim()) {
      setInputError('Name is required');
      return;
    }

    if (dialogType === 'zone' && zoneNames.includes(inputName.trim())) {
      setInputError('Zone name must be unique');
      return;
    }

    if (dialogType === 'borderLine' && borderLineNames.includes(inputName.trim())) {
      setInputError('Border line name must be unique');
      return;
    }

    const color = getRandomColor();

    if (dialogType === 'zone') {
      dispatch({
        type: SET_ZONE_NAME,
        payload: {
          name: inputName.trim(),
          color
        }
      });
      dispatch({ type: POLYGON_TYPE, polygonType: 'zones' });
    } else {
      dispatch({
        type: SET_BORDER_LINE_NAME,
        payload: {
          name: inputName.trim(),
          color
        }
      });
      dispatch({ type: POLYGON_TYPE, polygonType: 'frisking_line' });
    }

    setOpenDialog(false);
    setInputName('');
    setInputError('');
  };

  const handleDeletePolygon = (type, index) => {
    dispatch({
      type: DELETE_POLYGON,
      payload: {
        type,
        index
      }
    });
  };

  const handleClearAll = () => {
    dispatch({ type: CLEAR_ALL_POLYGONS });
  };

  const handleSendData = async () => {
    try {
      setLoading(true);

      // Get completed polygons and their names
      const zonesData = zoneNames
        .map((zoneName) => {
          const coordinates = polygonData.zones?.[zoneName];
          return {
            name: zoneName,
            camera_id: cameraId,
            zone_type: 'zone',
            colourHex: zoneColors[zoneName],
            roi: coordinates
          };
        })
        .filter((zone) => zone.roi && zone.roi.length > 0);

      const bordersData = borderLineNames
        .map((borderName) => {
          const coordinates = polygonData.frisking_line?.[borderName];
          return {
            name: borderName,
            camera_id: cameraId,
            zone_type: 'border',
            colourHex: borderLineColors[borderName],
            roi: coordinates
          };
        })
        .filter((border) => border.roi && border.roi.length > 0);

      console.log('Zone Names:', zoneNames);
      console.log('Border Names:', borderLineNames);
      console.log('Zone Colors:', zoneColors);
      console.log('Border Colors:', borderLineColors);
      console.log('Polygon Data:', polygonData);

      const allZonesData = {
        camera_id: cameraId,
        zones: [...zonesData, ...bordersData]
      };

      console.log('Final data being sent:', allZonesData);

      const response = await createZones(allZonesData);

      if (response) {
        handleCloseAnnotate();
        dispatch({ type: CAMERA_ADDED });
        setSnackbarMessage('Zones saved successfully');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error('Error saving zones:', error);
      setSnackbarMessage(error.message || 'Error saving zones');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <div className="rounded-lg p-2 text-black w-full h-full scrollbar overflow-y-auto">
      {/* Buttons for polygon types */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          variant={currentPolygonType === 'zones' ? 'contained' : 'outlined'}
          onClick={() => handlePolygonTypeSelect('zones')}
          color="primary"
        >
          ZONE
        </Button>
        <Button
          variant={currentPolygonType === 'frisking_line' ? 'contained' : 'outlined'}
          onClick={() => handlePolygonTypeSelect('frisking_line')}
          color="primary"
        >
          BORDER LINE
        </Button>
      </div>

      {/* Display zones and border lines */}
      <div className="p-4 rounded-lg bg-gray-50">
        {/* Display zones */}
        {zoneNames.length > 0 && (
          <div className="mb-4">
            <Typography variant="subtitle2" className="mb-2 text-gray-600">
              Zones
            </Typography>
            <div className="flex flex-wrap gap-2">
              {zoneNames.map((name, index) => (
                <div key={name} className="flex items-center bg-white px-3 py-1.5 rounded-md shadow-sm">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: zoneColors[name] }} />
                  <Typography variant="body2" className="mr-2">
                    {name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDeletePolygon('zones', index)}
                    className="text-gray-500 hover:text-red-500 ml-1"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Display border lines */}
        {borderLineNames.length > 0 && (
          <div className="mb-4">
            <Typography variant="subtitle2" className="mb-2 text-gray-600">
              Border Lines
            </Typography>
            <div className="flex flex-wrap gap-2">
              {borderLineNames.map((name, index) => (
                <div key={name} className="flex items-center bg-white px-3 py-1.5 rounded-md shadow-sm">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: borderLineColors[name] }} />
                  <Typography variant="body2" className="mr-2">
                    {name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDeletePolygon('frisking_line', index)}
                    className="text-gray-500 hover:text-red-500 ml-1"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end space-x-4 mt-6">
        <Button variant="outlined" color="error" onClick={handleClearAll} startIcon={<DeleteIcon />} disabled={loading}>
          Clear All
        </Button>

        <Button
          variant="contained"
          onClick={handleSendData}
          disabled={loading}
          style={{
            backgroundColor: loading ? 'gray' : '#4CAF50',
            color: 'white'
          }}
        >
          {loading ? <CircularProgress size={24} className="text-white" /> : 'Save All Polygons'}
        </Button>
      </div>

      {/* Name Input Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setInputName('');
          setInputError('');
        }}
      >
        <DialogTitle>{dialogType === 'zone' ? 'Enter Zone Name' : 'Enter Border Line Name'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={dialogType === 'zone' ? 'Zone Name' : 'Border Line Name'}
            fullWidth
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            error={!!inputError}
            helperText={inputError}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setInputName('');
              setInputError('');
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleNameSubmit} variant="contained" color="primary">
            {dialogType === 'zone' ? 'Add Zone' : 'Add Border Line'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MiddleController;
